import type { AutoIsoModel } from '../types/AutoIsoModel';
import type { GhostFilterConfig } from '../config/ghostFilter';

/**
 * Ghost-entity filter — pure logic, no React. Stateful across ticks via the
 * `GhostFilterState` object the caller provides; the caller owns the state
 * (typically as a useRef in a component) so this module remains testable in
 * isolation.
 *
 * On each tick:
 *   1. For each tracked entity, compare its current position to the last
 *      position we saw for that ID. If the Euclidean delta exceeds
 *      `positionEpsilon`, the entity has "moved" and its lastMoveTime is
 *      bumped to `nowMs`.
 *   2. If the entity hasn't moved beyond epsilon for `idleMs` ms, it's a ghost.
 *   3. Exempt types (e.g. BallOrPuck) have their lastMoveTime refreshed even
 *      when stationary so they never become ghosts.
 *   4. Entries for IDs absent from the current tick are pruned (memory hygiene).
 *
 * The function's return splits the data so the caller can act on either mode
 * (config.mode):
 *   - 'remove': liveEntities excludes ghosts (typical case).
 *   - 'dim'   : liveEntities contains every entity; the caller renders all of
 *               them but applies config.dimOpacity to those in `ghostIds`.
 */

export interface GhostFilterState {
  history: Map<
    string,
    {
      lastMoveTime: number;
      lastPos: { x: number; y: number; z: number };
      isGhost: boolean;
    }
  >;
}

export interface GhostFilterResult {
  liveEntities: AutoIsoModel[];
  ghostIds: Set<string>;
}

export function createGhostFilterState(): GhostFilterState {
  return { history: new Map() };
}

export function applyGhostFilter(
  entities: AutoIsoModel[],
  config: GhostFilterConfig,
  state: GhostFilterState,
  nowMs: number = Date.now(),
): GhostFilterResult {
  if (!config.enabled) {
    return { liveEntities: entities, ghostIds: new Set() };
  }

  const ghostIds = new Set<string>();
  const seenIds = new Set<string>();

  for (const entity of entities) {
    const id = entity.id.toString();
    seenIds.add(id);
    if (!entity.pos) continue;

    const isExempt = config.exemptObjectTypes.includes(entity.type);
    const prev = state.history.get(id);

    if (!prev) {
      // First sighting — seed the history so the entity isn't ghosted on entry.
      state.history.set(id, { lastMoveTime: nowMs, lastPos: { ...entity.pos }, isGhost: false });
      continue;
    }

    const dx = entity.pos.x - prev.lastPos.x;
    const dy = entity.pos.y - prev.lastPos.y;
    const delta = Math.hypot(dx, dy);

    // Hysteresis: living entities re-arm on positionEpsilon (small) movement;
    // ghosts only wake on wakeEpsilon (larger) movement. The asymmetric
    // thresholds prevent sub-epsilon jitter from oscillating an entity
    // between live and ghost states — exactly the source of the flicker.
    const liveThreshold = prev.isGhost ? config.wakeEpsilon : config.positionEpsilon;
    const moved = delta > liveThreshold;

    if (moved) {
      prev.lastMoveTime = nowMs;
      prev.lastPos = { ...entity.pos };
      prev.isGhost = false;
    } else if (isExempt) {
      // Ball/puck refresh — keeps them from ever appearing in ghostIds even
      // during legitimately stationary moments (dead ball, free-throw setup).
      prev.lastMoveTime = nowMs;
      prev.isGhost = false;
    } else if (prev.isGhost || nowMs - prev.lastMoveTime > config.idleMs) {
      // Either already ghosted (and motion didn't exceed wakeEpsilon) or
      // newly crosses the idle threshold. Either way → ghost on this tick.
      prev.isGhost = true;
      ghostIds.add(id);
    }
  }

  // Prune history entries for entities that vanished from the feed (subs,
  // ejections, end-of-period roster changes).
  for (const id of state.history.keys()) {
    if (!seenIds.has(id)) state.history.delete(id);
  }

  const liveEntities =
    config.mode === 'remove'
      ? entities.filter((e) => !ghostIds.has(e.id.toString()))
      : entities;

  return { liveEntities, ghostIds };
}
